import React, { useState, useEffect, useRef } from 'react';
import { useTranslate } from '../../hooks/useTranslate';
import VideoPlayerModal from './VideoPlayerModal';

const STORAGE_KEY = 'dd-training-welcome-dismissed';

const ALL_VIDEO_IDS = ['tv-001', 'tv-002', 'tv-003', 'tv-004', 'tv-005', 'tv-006', 'tv-007', 'tv-008'];

interface WelcomeBannerProps {
    isFirstLogin?: boolean;
}

const WelcomeBanner: React.FC<WelcomeBannerProps> = ({ isFirstLogin = false }) => {
    const { t } = useTranslate();
    const [dismissed, setDismissed] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');
    const [isOpen, setIsOpen] = useState(false);
    const [currentVideoId, setCurrentVideoId] = useState('tv-001');
    const hasAutoOpened = useRef(false);

    // Auto-open demo video for first-time users (overrides banner dismissed state)
    useEffect(() => {
        if (isFirstLogin && !hasAutoOpened.current) {
            hasAutoOpened.current = true;
            setIsOpen(true);
        }
    }, [isFirstLogin]);

    const handleClose = () => {
        setIsOpen(false);
        setCurrentVideoId('tv-001');
        if (isFirstLogin) {
            dismiss();
        }
    };

    const dismiss = () => {
        localStorage.setItem(STORAGE_KEY, 'true');
        setDismissed(true);
    };

    return (
        <>
            {!dismissed && (
                <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-5 mb-6 flex items-center gap-5 shadow-sm">
                    <div className="text-4xl flex-shrink-0">🎓</div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-base mb-1">
                            {t('Welcome to Data Doctor!')}
                        </h3>
                        <p className="text-gray-600 text-sm">
                            {t('Get started with a quick tour')}
                        </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setIsOpen(true)}
                            className="px-4 py-2 bg-[#D31145] text-white text-sm font-medium rounded-lg hover:bg-[#b50e3a] transition-colors"
                        >
                            ▶ {t('Watch Now')}
                        </button>
                        <button
                            type="button"
                            onClick={dismiss}
                            className="px-4 py-2 text-gray-500 text-sm font-medium hover:text-gray-700 transition-colors"
                        >
                            {t('Skip for Now')}
                        </button>
                    </div>
                </div>
            )}

            <VideoPlayerModal
                videoId={currentVideoId}
                allVideoIds={ALL_VIDEO_IDS}
                isOpen={isOpen}
                onClose={handleClose}
                onSelectVideo={setCurrentVideoId}
            />
        </>
    );
};

export default WelcomeBanner;
